import * as THREE from 'three';
import { crownGeo } from './tree-models.js';
import { FLAG_BLOCK, FLAG_BRIDGE, FLAG_WATER, RESOURCES, TERRAIN, type GameMap, type ResourceKind } from '@sunderfen/shared';
import type { Look } from './look.js';
import { stylize } from './stylize.js';

/** How many walkers can part the grass at once. */
export const PUSHERS = 6;
/**
 * Shared wind for every swaying material. uWind is the steady breeze; uGust (0..1) rolls waves of
 * wind across the land along uWindDir; uPush holds walkers ([x, z, radius, strength]) that part
 * grass and reeds around their feet.
 */
export const wind = {
  uTime: { value: 0 }, uWind: { value: 1 }, uGust: { value: 0.3 },
  uWindDir: { value: new THREE.Vector2(0.9, 0.44).normalize() },
  uPush: { value: Array.from({ length: PUSHERS }, () => new THREE.Vector4(0, 0, 0, 0)) },
};

/**
 * Make a material sway in the wind. Displacement grows with height above the mesh origin. The
 * idle sway is phased by each instance's position so neighbours don't move in lockstep; gusts and
 * walkers push in world space (after the instance transform), so every tuft bends the same way.
 */
export function addWind(mat: THREE.Material, amp: number, base = 0, push = false) {
  mat.onBeforeCompile = (shader: { uniforms: Record<string, { value: unknown }>; vertexShader: string }) => {
    Object.assign(shader.uniforms, wind);
    shader.vertexShader = `uniform float uTime;\nuniform float uWind;\nuniform float uGust;\nuniform vec2 uWindDir;\nuniform vec4 uPush[${PUSHERS}];\n` + shader.vertexShader
      .replace('#include <begin_vertex>', `#include <begin_vertex>
      #ifdef USE_INSTANCING
        vec2 ip = vec2(instanceMatrix[3].x, instanceMatrix[3].z);
      #else
        vec2 ip = vec2(modelMatrix[3].x, modelMatrix[3].z);
      #endif
      float ph = ip.x * 0.37 + ip.y * 0.23;
      float sw = sin(uTime * 1.6 + ph) * 0.55 + sin(uTime * 2.9 + ph * 1.7) * 0.2 + sin(uTime * 0.55 + ip.x * 0.05) * 0.45;
      float hf = max(position.y + ${base.toFixed(2)}, 0.0);
      transformed.x += sw * hf * ${amp.toFixed(3)} * uWind;
      transformed.z += sw * hf * ${(amp * 0.6).toFixed(3)} * uWind;
      // a gust front rolling across the land, with a flutter riding on it
      float gw = 0.5 + 0.5 * sin(dot(ip, uWindDir) * 0.16 - uTime * 1.7);
      float gust = uGust * gw * gw * (0.85 + 0.15 * sin(uTime * 7.0 + ph * 3.0));
      vec3 hmOff = vec3(uWindDir.x, -0.12, uWindDir.y) * gust * hf * ${(amp * 2.4).toFixed(3)};
      ${push ? `for (int i = 0; i < ${PUSHERS}; i++) {
        vec2 dv = ip - uPush[i].xy;
        float dd = length(dv);
        float k = (1.0 - smoothstep(0.0, uPush[i].z, dd)) * uPush[i].w;
        hmOff += vec3(dv / max(dd, 0.05) * 0.55, -0.35) * k * hf;
      }` : ''}`)
      .replace('#include <project_vertex>', `vec4 mvPosition = vec4( transformed, 1.0 );
      #ifdef USE_INSTANCING
        mvPosition = instanceMatrix * mvPosition;
      #endif
      mvPosition.xyz += hmOff;
      mvPosition = modelViewMatrix * mvPosition;
      gl_Position = projectionMatrix * mvPosition;`);
  };
  mat.customProgramCacheKey = () => `wind-${amp}-${base}-${push ? 'p' : ''}`;
}

function rng(seed: number) { return () => { seed |= 0; seed = (seed + 0x6d2b79f5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }

/** A tuft of blades with a dark root and bright tips; normals point up so it lights like the ground. */
function tuftGeometry(blades: number, height: number, width: number, lean: number) {
  const pos: number[] = [], col: number[] = [], nor: number[] = [];
  for (let i = 0; i < blades; i++) {
    const a = (i / blades) * Math.PI * 2 + i * 0.7, h = height * (0.75 + ((i * 37) % 10) / 30);
    const cx = Math.cos(a), cz = Math.sin(a), px = -cz * width, pz = cx * width;
    // both windings, normals up: lit like the ground from every side, no back-face darkening
    pos.push(-px, 0, -pz, px, 0, pz, cx * lean, h, cz * lean, px, 0, pz, -px, 0, -pz, cx * lean, h, cz * lean);
    for (let k = 0; k < 2; k++) col.push(0.62, 0.62, 0.62, 0.7, 0.7, 0.7, 1.25, 1.25, 1.12);
    for (let k = 0; k < 6; k++) nor.push(0, 1, 0);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  return g;
}

export interface FoliageSet { meshes: THREE.InstancedMesh[]; setDensity(f: number): void }

/**
 * Grass, flowers, reeds, bushes, pebbles and mushrooms, scattered from the map once and drawn
 * as one instanced mesh per kind. Instances are shuffled, so lowering `count` thins them
 * evenly across the map: that is how quality presets scale density for free.
 */
/** How many trees stand within 3 tiles of each tile (bushes grow near trees). Computed once per map. */
export function treeDensity(map: GameMap) {
  const treeNear = new Uint8Array(map.width * map.height);
  for (const o of map.objects) if (o.type === 'resource' && RESOURCES[o.kind as ResourceKind]?.family === 'tree')
    for (let dz = -3; dz <= 3; dz++) for (let dx = -3; dx <= 3; dx++) if (map.inBounds(o.x + dx, o.z + dz)) treeNear[map.idx(o.x + dx, o.z + dz)] = Math.min(255, treeNear[map.idx(o.x + dx, o.z + dz)] + 1);
  return treeNear;
}

/** Shared geometry and materials for every chunk's foliage (compiled once). */
type Kit = { geo: THREE.BufferGeometry; mat: THREE.Material };
let KITS: Record<'grass' | 'reed' | 'flower' | 'bush' | 'pebble' | 'mush' | 'stem', Kit> | null = null;
function kits() {
  if (KITS) return KITS;
  const k = (geo: THREE.BufferGeometry, mat: THREE.Material, windAmp?: number, base = 0, push = false): Kit => { if (windAmp) addWind(mat, windAmp, base, push); stylize(mat, { rim: false }); return { geo, mat }; };
  const flowerGeo = new THREE.OctahedronGeometry(0.055, 0); flowerGeo.translate(0, 0.26, 0);
  const bushGeo = crownGeo(0.42, { clumps: 5, squash: 0.72, spread: 0.6, warm: 0.2, seed: 70 }); bushGeo.translate(0, 0.25, 0);
  const mushGeo = new THREE.ConeGeometry(0.11, 0.09, 6); mushGeo.translate(0, 0.15, 0);
  const stemGeo = new THREE.CylinderGeometry(0.025, 0.03, 0.12, 5); stemGeo.translate(0, 0.06, 0);
  KITS = {
    grass: k(tuftGeometry(6, 0.3, 0.055, 0.1), new THREE.MeshLambertMaterial({ vertexColors: true }), 0.16, 0, true),
    reed: k(tuftGeometry(4, 0.8, 0.045, 0.12), new THREE.MeshLambertMaterial({ vertexColors: true }), 0.12, 0, true),
    flower: k(flowerGeo, new THREE.MeshLambertMaterial({ emissive: 0x222222 }), 0.14, 0, true),
    bush: k(bushGeo, new THREE.MeshLambertMaterial({ vertexColors: true }), 0.035, 0.2),
    pebble: k(new THREE.DodecahedronGeometry(0.09, 0), new THREE.MeshLambertMaterial()),
    mush: k(mushGeo, new THREE.MeshLambertMaterial()),
    stem: k(stemGeo, new THREE.MeshLambertMaterial()),
  };
  return KITS;
}

/**
 * Grass, reeds, flowers, bushes, pebbles and toadstools for one rectangle of tiles (a chunk).
 * Seeded by the rectangle, so the same chunk always grows the same plants.
 */
export function buildFoliage(map: GameMap, look: Look, tileColor: number[][], blocked: (t: number) => boolean, treeNear: Uint8Array, x0 = 0, z0 = 0, x1 = map.width, z1 = map.height): FoliageSet {
  const r = rng(4242 + x0 * 7919 + z0 * 104729);
  const regionKey = (t: number) => map.regions.find(x => x.id === map.region[t])?.key ?? '*';
  const nearWater = (x: number, z: number) => { for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) { if (map.inBounds(x + dx, z + dz) && (map.flags[map.idx(x + dx, z + dz)] & FLAG_WATER)) return true; } return false; };

  type Item = { x: number; z: number; s: number; rot: number; c: THREE.Color; tilt?: number };
  const grass: Item[] = [], flowers: Item[] = [], reeds: Item[] = [], bushes: Item[] = [], pebbles: Item[] = [], mush: Item[] = [];
  const tmpC = new THREE.Color();
  for (let z = Math.max(1, z0); z < Math.min(map.height - 1, z1); z++) for (let x = Math.max(1, x0); x < Math.min(map.width - 1, x1); x++) {
    const t = map.idx(x, z), f = map.flags[t], ter = map.terrain[t], key = regionKey(t);
    if (f & (FLAG_WATER | FLAG_BRIDGE) || blocked(t)) continue;
    const c = tileColor[t];
    const ground = () => tmpC.setRGB(c[0], c[1], c[2], THREE.SRGBColorSpace).clone();
    const at = (k: number) => ({ x: x + 0.1 + r() * 0.8, z: z + 0.1 + r() * 0.8, s: k, rot: r() * Math.PI * 2 });
    if (ter === TERRAIN.grass && !(f & FLAG_BLOCK)) {
      const n = key === 'ashen' ? 1 : key === 'village' ? 2 : 3;
      for (let i = 0; i < n; i++) { const it = at(0.8 + r() * 0.6); grass.push({ ...it, c: ground().multiplyScalar(0.95 + r() * 0.3) }); }
      if (r() < (key === 'downs' || key === 'village' || key === 'lake' ? 0.32 : 0.12)) {
        const pal = look.flowers[key] ?? look.flowers['*'];
        const n2 = 1 + Math.floor(r() * 3), col = pal[Math.floor(r() * pal.length)];
        for (let i = 0; i < n2; i++) flowers.push({ ...at(0.8 + r() * 0.5), c: new THREE.Color(col) });
      }
      if (treeNear[t] >= 2 && r() < 0.09) bushes.push({ ...at(0.7 + r() * 0.6), c: new THREE.Color(look.bush).multiplyScalar(0.8 + r() * 0.4) });
      if ((key === 'fen' || key === 'grove') && r() < 0.05) mush.push({ ...at(0.7 + r() * 0.6), c: new THREE.Color(r() < 0.5 ? 0xb8452e : 0xc9a46a) });
    }
    if ((ter === TERRAIN.grass || ter === TERRAIN.sand || ter === TERRAIN.mud) && nearWater(x, z) && key !== 'ashen' && r() < 0.55) {
      for (let i = 0; i < 3; i++) reeds.push({ ...at(0.8 + r() * 0.6), c: new THREE.Color(look.reed).multiplyScalar(0.8 + r() * 0.35) });
    }
    if ((ter === TERRAIN.sand && r() < 0.35) || (ter === TERRAIN.path && r() < 0.12) || (ter === TERRAIN.rock && !(f & FLAG_BLOCK) && r() < 0.4)) {
      pebbles.push({ ...at(0.5 + r() * 1), c: new THREE.Color(look.pebble).multiplyScalar(0.75 + r() * 0.4) });
    }
  }
  const shuffle = <T,>(a: T[]) => { for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };
  const meshes: THREE.InstancedMesh[] = [];
  const totals: number[] = [];
  const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler(), v = new THREE.Vector3(), sc = new THREE.Vector3();
  function make(items: Item[], kit: Kit, y: (it: Item) => number, scale: (it: Item) => [number, number, number], shadows: boolean, mix = true) {
    if (mix) shuffle(items);
    const im = new THREE.InstancedMesh(kit.geo, kit.mat, Math.max(1, items.length));
    items.forEach((it, i) => {
      e.set(it.tilt ?? 0, it.rot, 0); q.setFromEuler(e);
      v.set(it.x, map.heightAt(it.x, it.z) + y(it), it.z); sc.set(...scale(it));
      im.setMatrixAt(i, m4.compose(v, q, sc));
      im.setColorAt(i, it.c);
    });
    im.count = items.length; im.castShadow = shadows; im.receiveShadow = true;
    // cull the chunk as a whole: a sphere around its rectangle
    im.frustumCulled = true;
    const cx = (x0 + x1) / 2, cz = (z0 + z1) / 2, rad = Math.hypot(x1 - x0, z1 - z0) / 2 + 2;
    im.boundingSphere = new THREE.Sphere(new THREE.Vector3(cx, map.heightAt(Math.min(cx, map.width - 1), Math.min(cz, map.height - 1)), cz), rad + 6);
    meshes.push(im); totals.push(items.length);
    return im;
  }
  const K = kits();
  make(grass, K.grass, () => -0.02, it => [it.s, it.s, it.s], false);
  make(reeds, K.reed, () => -0.02, it => [it.s, it.s, it.s], false);
  make(flowers, K.flower, () => -0.02, it => [it.s, it.s * 0.7, it.s], false);
  make(bushes, K.bush, () => 0, it => [it.s * 1.2, it.s * 0.8, it.s], true);
  make(pebbles, K.pebble, () => 0.02, it => [it.s, it.s * 0.6, it.s * 0.9], false);
  make(mush, K.mush, () => 0, it => [it.s, it.s, it.s], false, false);
  make(mush.map(m => ({ ...m, c: new THREE.Color(0xeee6d0) })), K.stem, () => 0, it => [it.s, it.s, it.s], false, false);
  return {
    meshes,
    setDensity(f: number) { meshes.forEach((m, i) => { m.count = Math.floor(totals[i] * (i <= 2 ? f : Math.min(1, f + 0.3))); }); },
  };
}
